import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '../hooks/useAuth'
import { apiGet } from '../services/api'
import {
  BookOpen, FileText, Video, Award, TrendingUp, Clock,
  Users, Star, Activity, Calendar, ArrowRight, Zap
} from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts'

export default function Dashboard() {
  const { user } = useAuthStore()
  const [stats, setStats] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    apiGet('/dashboard/stats').then(data => {
      setStats(data)
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [])

  const mockStats = {
    coursesInProgress: 4,
    completedCourses: 12,
    averageScore: 87,
    certificates: 8,
    upcomingExams: 2,
    studyHours: 156,
    weeklyProgress: [
      { day: 'Mon', hours: 3 },
      { day: 'Tue', hours: 5 },
      { day: 'Wed', hours: 2 },
      { day: 'Thu', hours: 4 },
      { day: 'Fri', hours: 6 },
      { day: 'Sat', hours: 3 },
      { day: 'Sun', hours: 1 },
    ],
    subjectDistribution: [
      { name: 'Math', value: 30 },
      { name: 'Science', value: 25 },
      { name: 'Programming', value: 20 },
      { name: 'Language', value: 15 },
      { name: 'Other', value: 10 },
    ]
  }

  const data = stats || mockStats
  const colors = ['#1e40af', '#7c3aed', '#10b981', '#f59e0b', '#ef4444']

  const quickActions = [
    { icon: BookOpen, label: 'Browse Courses', path: '/courses', color: 'bg-blue-500/20 text-blue-400' },
    { icon: FileText, label: 'Take Exam', path: '/exams', color: 'bg-purple-500/20 text-purple-400' },
    { icon: Video, label: 'Join Live Class', path: '/live-class', color: 'bg-green-500/20 text-green-400' },
    { icon: Award, label: 'Certificates', path: '/certificates', color: 'bg-amber-500/20 text-amber-400' },
  ]

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Welcome */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Welcome back, {user?.name}!</h1>
          <p className="text-slate-500 mt-1">Here's what's happening in your learning journey.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-3 py-1 bg-primary/20 text-primary rounded-full text-sm font-medium">
            {user?.subscription?.toUpperCase()} PLAN
          </span>
          {user?.role === 'admin' && (
            <span className="px-3 py-1 bg-danger/20 text-danger rounded-full text-sm font-medium">
              ADMIN
            </span>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {quickActions.map((action) => {
          const Icon = action.icon
          return (
            <Link
              key={action.path}
              to={action.path}
              className="glass rounded-xl p-4 hover:bg-slate-800/50 transition-colors group"
            >
              <div className={`w-10 h-10 rounded-lg ${action.color} flex items-center justify-center mb-3`}>
                <Icon className="w-5 h-5" />
              </div>
              <p className="font-medium text-sm">{action.label}</p>
              <ArrowRight className="w-4 h-4 text-slate-600 group-hover:text-white transition-colors mt-2" />
            </Link>
          )
        })}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: BookOpen, label: 'Courses in Progress', value: data.coursesInProgress, trend: '+2 this week' },
          { icon: FileText, label: 'Average Score', value: `${data.averageScore}%`, trend: '+5% vs last month' },
          { icon: Award, label: 'Certificates', value: data.certificates, trend: '2 pending' },
          { icon: Clock, label: 'Study Hours', value: data.studyHours, trend: 'This month' },
        ].map((stat, i) => {
          const Icon = stat.icon
          return (
            <div key={i} className="glass rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                  <Icon className="w-5 h-5 text-primary" />
                </div>
                <TrendingUp className="w-4 h-4 text-success" />
              </div>
              <p className="text-2xl font-bold">{stat.value}</p>
              <p className="text-sm text-slate-500 mt-1">{stat.label}</p>
              <p className="text-xs text-success mt-2">{stat.trend}</p>
            </div>
          )
        })}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="glass rounded-xl p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary" />
            Weekly Study Hours
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={data.weeklyProgress}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="day" stroke="#64748b" />
              <YAxis stroke="#64748b" />
              <Tooltip 
                contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                itemStyle={{ color: '#f8fafc' }}
              />
              <Bar dataKey="hours" fill="#1e40af" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="glass rounded-xl p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Star className="w-5 h-5 text-primary" />
            Subject Distribution
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={data.subjectDistribution}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={5}
                dataKey="value"
              >
                {data.subjectDistribution.map((_: any, index: number) => (
                  <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                itemStyle={{ color: '#f8fafc' }}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-3 mt-4">
            {data.subjectDistribution.map((item: any, i: number) => (
              <div key={item.name} className="flex items-center gap-1.5 text-xs">
                <div className="w-3 h-3 rounded-full" style={{ background: colors[i] }}></div>
                <span className="text-slate-400">{item.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Upcoming */}
      <div className="glass rounded-xl p-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-primary" />
          Upcoming Events
        </h3>
        <div className="space-y-3">
          {[
            { title: 'Advanced Mathematics Final Exam', type: 'exam', time: 'Tomorrow, 10:00 AM', icon: FileText },
            { title: 'Python Programming Live Class', type: 'live', time: 'Today, 2:00 PM', icon: Video },
            { title: 'Science Quiz - Random', type: 'quiz', time: 'Any moment...', icon: Zap },
          ].map((event, i) => {
            const Icon = event.icon
            return (
              <div key={i} className="flex items-center gap-4 p-3 bg-slate-800/50 rounded-lg">
                <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-sm">{event.title}</p>
                  <p className="text-xs text-slate-500">{event.time}</p>
                </div>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  event.type === 'exam' ? 'bg-danger/20 text-danger' :
                  event.type === 'live' ? 'bg-success/20 text-success' :
                  'bg-warning/20 text-warning'
                }`}>
                  {event.type.toUpperCase()}
                </span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
